import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CloudSun } from "lucide-react";
import { api, setPageMeta, setJsonLd, clubListItem } from "@/lib/api";
import { ClubRow } from "@/components/ClubRow";
import { Reveal } from "@/components/Motion";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { stateName } from "@/lib/geo";
import { ChecklistMagnet } from "@/components/ChecklistMagnet";
import { cityCollectionForDirectory, productsForCollection } from "@/lib/shopCatalog";

// Audit NRC-091: supplement editorial FAQs with data-driven ones (never invented, always derived from listings)
function generatedFaqs(city, clubs) {
  const faqs = [];
  faqs.push({
    q: `How many run clubs are in ${city.name}?`,
    a: `${clubs.length} run club${clubs.length === 1 ? " is" : "s are"} currently listed for ${city.name} in the Ninja Run Club directory. Every listing is human-reviewed, and verified clubs show a last-verified date on their profile.`,
  });
  const beginner = clubs.filter((c) => (c.tags || []).some((t) => /beginner|no-drop|all paces|walk/i.test(t))).length;
  if (beginner > 0) {
    faqs.push({
      q: `Are ${city.name} run clubs beginner friendly?`,
      a: `Yes — ${beginner} of the ${clubs.length} listed ${city.name} clubs are tagged beginner-friendly, no-drop, or all-paces. Look for published pace ranges on a club's profile before your first visit.`,
    });
  }
  const free = clubs.filter((c) => (c.tags || []).some((t) => /free/i.test(t))).length;
  if (free > 0) {
    faqs.push({
      q: `Do ${city.name} run clubs cost money?`,
      a: `${free} of the listed ${city.name} clubs are tagged free. Most community run clubs cost nothing to join — paid coached programs are the exception and publish pricing up front.`,
    });
  }
  faqs.push({
    q: `How do I join a run club in ${city.name}?`,
    a: `Pick a club from the list above, confirm this week's run on the club's own Instagram (schedules shift), and arrive ten minutes early. Most community clubs need no signup — just tell the organizer it is your first time.`,
  });
  return faqs;
}

const CityGearModule = ({ col, cityName }) => {
  const products = productsForCollection(col);
  const tee = products.find((p) => p.slug.startsWith("area-code-")) || products[0];
  const full = products.length > 1;
  return (
    <section className="px-5 lg:px-10 py-16 border-b border-white/10" data-testid="city-gear-section">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="font-mono-label text-white/40">CITY SERIES — THE {col.code} DROP</p>
          <h2 className="font-display text-3xl lg:text-4xl mt-3">{cityName.toUpperCase()} RUNNING APPAREL.</h2>
          <p className="text-white/50 text-sm mt-3 max-w-xl">
            {full
              ? `Limited ${cityName} gear from the NRC City Series — the ${col.code} area-code tee, the ${cityName} skyline running shirt, and the five-panel running hat. 100 pieces per design, retired when sold out.`
              : `The ${cityName} chapter of the NRC City Series opens with the ${col.code} area-code tee — a limited run of 100 pieces, retired when it sells out. Run your city.`}
          </p>
          <Link to={col.path} data-testid="city-gear-link" className="btn-invert inline-block bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white mt-6">
            SHOP {cityName.toUpperCase()} RUNNING APPAREL →
          </Link>
        </div>
        {tee && (
          <Link
            to={`/products/${tee.url_slug}`}
            data-testid="city-gear-tee"
            className="group border border-white/10 hover:border-white/50 transition-colors duration-200 flex items-center gap-5 p-4"
          >
            <img
              src={tee.image}
              alt={tee.seo_name}
              loading="lazy"
              width="640"
              height="640"
              className="h-32 w-32 lg:h-40 lg:w-40 object-cover shrink-0"
            />
            <div className="min-w-0">
              <p className="font-display text-xl lg:text-2xl leading-tight">{tee.seo_name.toUpperCase()}</p>
              <p className="font-mono-label text-white/40 mt-1.5">${tee.price} · LIMITED RUN OF {tee.drop_size || 100}</p>
              <p className="font-mono-label text-white/60 mt-4 group-hover:text-white transition-colors duration-200">SHOP THE {col.code} TEE →</p>
            </div>
          </Link>
        )}
      </div>
    </section>
  );
};

const MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [allCities, setAllCities] = useState([]);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/cities/${stateSlug}/${citySlug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [stateSlug, citySlug]);

  useEffect(() => {
    api.get("/cities").then((r) => setAllCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data) return;
    const { city, clubs } = data;
    const month = new Date().toLocaleString("en-US", { month: "long", year: "numeric" });
    // Audit NRC-015 description template; NRC-081 thin-city noindex until real coverage
    setPageMeta(
      `Best Run Clubs in ${city.name}, ${city.state} (2026) | Ninja Run Club`,
      `Find ${clubs.length} verified run club${clubs.length === 1 ? "" : "s"} in ${city.name}, ${city.state} — meeting days, paces, and routes worth running. Human-verified, updated ${month}.`,
      { noindex: clubs.length < 3 }
    );
    const faqs = [...(city.faqs || []), ...generatedFaqs(city, clubs)].slice(0, 7);
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `Best Run Clubs in ${city.name}, ${city.state}`,
        description: city.intro?.slice(0, 200),
        url: `https://ninjarunclub.com/run-clubs/${stateSlug}/${citySlug}`,
        numberOfItems: clubs.length,
        itemListElement: clubs.map((c, i) => clubListItem(c, i + 1)),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    return setJsonLd("city-jsonld", ld);
  }, [data, stateSlug, citySlug]);

  const siblings = useMemo(() => allCities.filter((c) => c.slug !== citySlug), [allCities, citySlug]);
  const stateSiblings = useMemo(() => siblings.filter((c) => c.state_slug === stateSlug), [siblings, stateSlug]);
  const otherStateSiblings = useMemo(() => siblings.filter((c) => c.state_slug !== stateSlug), [siblings, stateSlug]);

  if (notFound) {
    setPageMeta("City Page Not Found | Ninja Run Club", "", { noindex: true });
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
        <p className="font-display text-4xl">CITY PAGE COMING SOON.</p>
        <Link to="/clubs" className="font-mono-label link-underline text-white/60">SEARCH THE DIRECTORY INSTEAD</Link>
      </div>
    );
  }
  if (!data) return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;

  const { city, clubs } = data;
  const faqs = [...(city.faqs || []), ...generatedFaqs(city, clubs)].slice(0, 7);

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden bg-ink border-b border-white/10">
        <video
          poster="/video/run-13-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-label="Runners lapping a track at golden hour"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          data-testid="city-hero-video"
        >
          <source src={MOBILE ? "/video/run-13-mobile.mp4" : "/video/run-13.mp4"} type="video/mp4" />
          <source src="/video/run-13.webm" type="video/webm" />
        </video>
        <div className="relative px-5 lg:px-10 pt-10 pb-16">
          <Breadcrumbs
            light
            items={[["Home", "/"], ["Run Clubs", "/clubs"], [stateName(stateSlug), `/run-clubs/${stateSlug}`], [`${city.name}, ${city.state}`, `/run-clubs/${stateSlug}/${citySlug}`]]}
          />
          <Link to="/clubs" className="mt-6 inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
            <ArrowLeft size={13} /> ALL CITIES
          </Link>
          <Reveal>
            <p className="font-mono-label text-white/50 mt-8">{city.tagline?.toUpperCase()}</p>
            <h1 data-testid="city-title" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none">
              RUN {city.name.toUpperCase()}.
            </h1>
            <p className="text-white/70 max-w-2xl mt-6 text-sm lg:text-base">{city.intro}</p>
            {city.visitor_note && (
              <p className="text-white/50 max-w-2xl mt-4 text-sm border-l border-white/30 pl-4">{city.visitor_note}</p>
            )}
          </Reveal>
        </div>
      </header>

      {citySlug === "dallas" && (
        <Link to="/dallas" data-testid="dallas-chapter-banner" className="group flex items-center justify-between gap-4 bg-white text-ink px-5 lg:px-10 py-5 hover:bg-night hover:text-white border-b border-white/10 transition-colors duration-300">
          <div>
            <p className="font-mono-label opacity-50">NRC HOME CHAPTER</p>
            <p className="font-display text-xl lg:text-2xl mt-1">WE HOST OUR OWN WEEKLY RUNS IN DALLAS — SEE THE SCHEDULE.</p>
          </div>
          <ArrowUpRight size={22} className="shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
        </Link>
      )}

      <section className="px-5 lg:px-10 py-14" data-testid="city-clubs-section">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-display text-3xl lg:text-4xl">THE CLUBS ({clubs.length})</h2>
          <Link to="/submit" className="font-mono-label link-underline text-white/50">SUBMIT A CLUB</Link>
        </div>
        {clubs.map((c, i) => (
          <ClubRow key={c.slug} club={c} index={i} />
        ))}
      </section>

      <section className="px-5 lg:px-10 pb-14">
        <ChecklistMagnet source={`checklist-city-${citySlug}`} />
      </section>

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20" data-testid="city-routes-section">
        <Reveal>
          <p className="font-mono-label text-ink/40">WHERE TO RUN</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-4">THE ROUTES.</h2>
        </Reveal>
        <div className="mt-10">
          {(city.routes || []).map((r, i) => (
            <div key={r.name} className="grid grid-cols-12 gap-4 items-baseline border-t border-ink/10 py-6 last:border-b">
              <span className="col-span-1 font-mono-label text-ink/30">{String(i + 1).padStart(2, "0")}</span>
              <p className="col-span-11 md:col-span-4 font-display text-xl lg:text-2xl">{r.name}</p>
              <p className="col-span-11 col-start-2 md:col-span-2 font-mono-label text-ink/50">{r.distance}</p>
              <p className="col-span-11 col-start-2 md:col-span-5 text-ink/60 text-sm">{r.note}</p>
            </div>
          ))}
        </div>
        {city.weather_note && (
          <div className="mt-10 border border-ink/15 p-6 flex items-start gap-3 max-w-3xl">
            <CloudSun size={18} className="mt-0.5 shrink-0" />
            <p className="text-sm text-ink/70">{city.weather_note}</p>
          </div>
        )}
      </section>

      {faqs.length > 0 && (
        <section className="px-5 lg:px-10 py-20 border-b border-white/10" data-testid="city-faq-section">
          <h2 className="font-display text-3xl lg:text-4xl mb-8">QUESTIONS RUNNERS ASK.</h2>
          <div className="max-w-3xl">
            {faqs.map((f, i) => (
              <details key={i} className="group border-b border-white/10 py-1" data-testid={`city-faq-${i}`}>
                <summary className="font-mono-label text-left text-white/80 hover:text-white cursor-pointer list-none py-4 flex items-center justify-between gap-4">
                  {f.q}
                  <span aria-hidden="true" className="text-white/40 group-open:rotate-45 transition-transform duration-200 text-lg leading-none">+</span>
                </summary>
                <p className="text-white/60 text-sm pb-5 max-w-2xl">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {cityCollectionForDirectory(stateSlug, citySlug) && (
        <CityGearModule col={cityCollectionForDirectory(stateSlug, citySlug)} cityName={city.name} />
      )}

      {siblings.length > 0 && (
        <section className="px-5 lg:px-10 py-16 border-b border-white/10" data-testid="city-siblings-section">
          <h2 className="font-mono-label text-white/50">RUN CLUBS IN OTHER CITIES</h2>
          {stateSiblings.length > 0 && (
            <div className="mt-5">
              <p className="font-mono-label text-white/30">MORE IN {stateName(stateSlug).toUpperCase()}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {stateSiblings.map((c) => (
                  <Link key={c.slug} to={`/run-clubs/${c.state_slug}/${c.slug}`} data-testid={`sibling-city-${c.slug}`} className="font-mono-label border border-white/20 px-4 py-2.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200">
                    {c.name.toUpperCase()}
                  </Link>
                ))}
                <Link to={`/run-clubs/${stateSlug}`} className="font-mono-label border border-white/40 px-4 py-2.5 hover:bg-white hover:text-ink transition-colors duration-200">
                  ALL {stateName(stateSlug).toUpperCase()} →
                </Link>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-5">
            {otherStateSiblings.map((c) => (
              <Link key={c.slug} to={`/run-clubs/${c.state_slug}/${c.slug}`} className="font-mono-label border border-white/10 px-3 py-2 text-white/50 hover:bg-white hover:text-ink transition-colors duration-200">
                {c.name.toUpperCase()}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="px-5 lg:px-10 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <p className="font-display text-2xl lg:text-3xl">KNOW A {city.name.toUpperCase()} CLUB WE MISSED?</p>
          <p className="text-white/50 text-sm mt-2">Help us make this the most accurate directory in the city.</p>
        </div>
        <Link to="/submit" data-testid="city-submit-cta" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
          SUBMIT A CLUB
        </Link>
      </section>
    </div>
  );
}
